import React from "react";
import classnames from "classnames";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { IEventFilter } from "@formant/core/model/IEventFilter";
import { Uuid } from "@formant/core/model/Uuid";

import { useRouter } from "../../context/RouterContext";
import { useStores } from "../../context/StoreContext";
import { useLiveEventCountsByDevice } from "../../utils/useLiveEventCountsByDevice";
import { EventsButtonPassive } from "../common/EventsButtonPassive";
import { NoDataMessage } from "../common/NoDataMessage";
import { DeviceStatus } from "../DeviceStatus";
import * as styles from "./DeviceList.scss";
import { FavoriteToggle } from "./FavoriteToggle";
import { LinkList } from "./LinkList";
import { LinkListItem } from "./LinkListItem";

export interface IDeviceListProps {
    onClose: () => void;
    onDeleteDevice: (id: Uuid) => void;
}

const DeviceList = observer(function DeviceList(props: IDeviceListProps) {
    const { onClose } = props;
    const { routeTo } = useRouter();
    const { deviceDetailsStore, eventStore, onlineDevicesStore } = useStores();
    const online = onlineDevicesStore.online();
    const allDevicesFilter: IEventFilter = {
        deviceIds: deviceDetailsStore.recent()?.map(_ => _.id) || [],
        disableNullMatches: true,
        viewed: false,
        notificationEnabled: true,
        setsDeviceColor: true,
    };
    const counts = useLiveEventCountsByDevice(allDevicesFilter);

    const deviceItems = (deviceDetailsStore.recent() || []).map(device => {
        const id = defined(device.id);
        const deviceCount = counts && counts.counts[id];

        return (
            <LinkListItem
                data-testid={`sidebar-device-${device.id}`}
                key={id}
                icon={<FavoriteToggle id={id} />}
                title={device.name}
                to={routeTo.device({ id })}
                onClicked={onClose}
                className={classnames(
                    styles.device,
                    eventStore.hasNotificationsByDevice(allDevicesFilter, id) &&
                        styles["has-notifications"],
                )}
            >
                <DeviceStatus
                    id={id}
                    className={styles.status}
                    online={online?.has(id) || false}
                />
                <EventsButtonPassive
                    counts={deviceCount}
                    to={routeTo.unifiedDevice({ mode: "events", id })}
                    small={true}
                    className={styles["events-button"]}
                />
            </LinkListItem>
        );
    });

    return (
        <>
            <LinkList>{deviceItems}</LinkList>
            {deviceItems.length === 0 && (
                <NoDataMessage>No devices configured</NoDataMessage>
            )}
        </>
    );
});

export default DeviceList;
